import { FC } from 'react';

import _ from 'lodash';

import { concatClassNames, formatTime } from '../../../../utils/utilities';
import { getCustomLPTColumns } from '../../../../utils/data';
import { CoverDataItem, CustomMPIReport } from '../../../../utils/models';
import { APP_DATE_FORMAT } from '../../../../utils/constants';

import Conditional from '../../../common/conditional';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import css from './style.module.scss';

interface Props extends CustomMPIReport {
	showOverview: boolean;
	showSummary: boolean;
}

const MPIReportSummary: FC<Props> = (props) => {
	return (
		<div className={css.mpiReport}>
			<Conditional isVisible={props.showOverview}>
				<div className="container-fluid overflow-hidden">
					<div className="row">
						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									BG Contrast Medium Used
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.bgContrastMediumUsed}
								</div>
							</div>	

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Black Ink Batch No.
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.blackInkBatchNumber}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									White Contrast Batch No.
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.whiteContrastBatchNumber}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									MPI Equipment
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.equipment}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Surface Condition
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.surfaceCondition}
								</div>
							</div>
						</div>

						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Magnetic Medium Used
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.magneticMediumUsed}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Black Ink Exp. Date
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.blackInkExpiryDate ? formatTime(props.blackInkExpiryDate, APP_DATE_FORMAT) : 'N/A'}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									White Contrast Exp. Date
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.whiteContrastExpiryDate ? formatTime(props.whiteContrastExpiryDate, APP_DATE_FORMAT) : 'N/A'}
								</div>
							</div>
							
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Equipment Serial Number
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.equipmentSerialNumber ?? 'N/A'}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Current Used
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.currentUsed}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
							MPI Equipment Calibration Due Date
						</div>
						<div className={concatClassNames([css.mpiReportBody, 'col'])}>
							{props.equipmentCalibrationDueDate ? formatTime(props.equipmentCalibrationDueDate, APP_DATE_FORMAT) : 'N/A'}
						</div>
					</div>
				</div>

				<Conditional isVisible={false}>
					<div className={css.mpiReportSection}></div>
				</Conditional>

				<div className="container-fluid overflow-hidden">
					<div className="row">
						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Light Intensity
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.light}
								</div>
							</div>
							
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Lux Meter Model
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{(_.isString(props.modal) || !props.modal) ? props.modal : (props.modal as CoverDataItem).value}
								</div>
							</div>
						</div>

						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Stage of test
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.stageOfTest}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Lux Meter Serial No.
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.serialNumber}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Light Meter Calibration Due Date
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.calibrationDueDate ? formatTime(props.calibrationDueDate, APP_DATE_FORMAT) : 'N/A'}
								</div>
							</div>
						</div>

						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Date of Inspection
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.dateOfInspection ? formatTime(props.dateOfInspection, APP_DATE_FORMAT) : 'N/A'}
								</div>
							</div>
						</div>
					</div>
				</div>

				<Conditional isVisible={false}>
					<div className={css.mpiReportSection}></div>
				</Conditional>

				<div className="container-fluid overflow-hidden">
					<div className="row">
						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Surface Temperature.
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.surfaceTemperature}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Method
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.method}
								</div>
							</div>
							
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Pie Guage Used
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.pieGuageUsed ?? 'N/A'}
								</div>
							</div>
						</div>
						<div className="col">
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Material
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.material}
								</div>
							</div>

							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Technique
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.technique}
								</div>
							</div>
							
							<div className="row">
								<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
									Lifting Force Check
								</div>
								<div className={concatClassNames([css.mpiReportBody, 'col'])}>
									{props.liftingForceCheck ?? 'N/A'}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Conditional>

			<Conditional isVisible={false}>
				<div className={css.mpiReportSection}></div>
			</Conditional>

			<PDFTablePage data={props.data} columns={getCustomLPTColumns()} />

			<Conditional isVisible={false}>
				<div className={css.mpiReportSection}></div>
			</Conditional>

			<Conditional isVisible={false}>
				<div className="container-fluid overflow-hidden">
					<div className="row">
						<div className={concatClassNames([css.mpiReportHeader, 'col-5'])}>
							Summary
						</div>
						<div className={concatClassNames([css.mpiReportBody, 'col'])}>
							{props.summary}
						</div>
					</div>
				</div>
			</Conditional>
		</div>
	);
};

export default MPIReportSummary;
